// VociView.jsx — amm-v3 (A4, ondata 31): la vista "Voci & trasferte"
// (pagina 'voci') in React, flag-gated (shell_amm_v3_voci, default OFF).
//
// Replica 1:1 renderVoci di admin-module.js r.2720-2833: stesso titolo e
// sottotitolo, stessi bottoni ('Classifica voci esistenti', '+ Esporta CSV',
// frecce anno), 2 KPI, chips categoria (Tutte + 11 categorie), UNA tabella
// per categoria con movimenti (dipendente×12 mesi + Totale) e riga TOTALE
// categoria, stesso stato vuoto (col suffisso categoria e l'hint "Classifica").
//
// STATO CONDIVISO col v1 (parità di persistenza): anno su AMM.vociAnno (r.751),
// categoria su AMM.vociCategoria (r.755). COMMIT ATOMICO (tabelle e KPI
// cambiano solo a fetch conclusa).
//
// DEVIAZIONI DICHIARATE (v3 non bug-compatibile qui):
//  D-A4.1 doppio click su 'Classifica voci esistenti' = 1 richiesta (il v1
//         disabilita il bottone durante — parità; qui guardia ref + disabled).
//  D-A4.2 doppio click su '+ Esporta CSV' = 1 richiesta (guardia ref; il v1
//         NON guarda, r.2829).
//  D-A4.5 ricarica fallita con dati presenti ⇒ banner + Riprova, dati
//         CONSERVATI (famiglia D-A1.5/D-A3.2); a mount fresco errore pieno con
//         Riprova. Il v1 sostituirebbe la pagina con 'Errore nel caricamento'.

import React from 'react';
import { AM_NEW_TEXTS } from './api.js';
import {
  AV_TEXTS, MESI_S, CHIP_CATEGORIE,
  vociStato, raggruppaVoci, totaliMesiCategoria, labelCat, nomeRiga,
  emptyMessage, chipLabel, classificateToast,
  readVoci, classificaEsistenti, esportaCsvVoci,
} from './voci-api.js';

function StatoBox({ error, onRetry }) {
  if (error) {
    const msg = error && error.message ? error.message : String(error);
    return (
      <div role="alert" className="bg-white border rounded-lg p-6 text-center" style={{ borderColor: '#fecaca', background: '#fef2f2' }}>
        <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>{'⚠ ' + msg}</div>
        <button type="button" style={{ marginTop: 12, background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }} onClick={onRetry}>{AM_NEW_TEXTS.riprova}</button>
      </div>
    );
  }
  return <div className="text-slate-400 text-sm">Caricamento…</div>;
}

// KPI markup VERBATIM v1 (AMM.kpi r.1345-1347) — replicato in JSX (pin nel test)
function Kpi({ label, val, color }) {
  return (
    <div className="bg-white border rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-xl font-semibold mt-1" style={{ color }}>{val}</div>
    </div>
  );
}

export function VociView({ win, tick }) {
  const A = win.AMM;
  const [pending, setPendingState] = React.useState(() => vociStato(A));
  const setPending = (next) => {
    A.vociAnno = next.anno;         // write-through (r.2812-2813)
    A.vociCategoria = next.categoria; // write-through (r.2815)
    setPendingState(next);
  };
  const [dati, setDati] = React.useState({ loading: true, error: null, vista: null });
  const [reloadN, setReloadN] = React.useState(0);
  const [classificando, setClassificando] = React.useState(false);
  const busyClassRef = React.useRef(false);
  const busyCsvRef = React.useRef(false);

  React.useEffect(() => {
    let vivo = true;
    const anno = pending.anno;
    const categoria = pending.categoria;
    setDati((d) => ({ ...d, loading: true, error: null }));
    readVoci(win, anno, categoria).then(
      (r) => { if (vivo) setDati({ loading: false, error: null, vista: { anno, categoria, ...r } }); },
      (e) => { if (vivo) setDati((d) => ({ ...d, loading: false, error: e })); }
    );
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, tick, reloadN, pending.anno, pending.categoria]);

  const vista = dati.vista;
  const anno = vista ? vista.anno : pending.anno;
  const categoria = vista ? vista.categoria : pending.categoria;
  const f2 = (n) => A.fmtEur2(n);
  const gruppi = vista ? raggruppaVoci(vista.righe) : [];

  const onClassifica = async () => {
    if (busyClassRef.current) return;
    busyClassRef.current = true;
    setClassificando(true);
    try {
      const res = await classificaEsistenti(win);
      A.toast(classificateToast(res));
      setReloadN((n) => n + 1); // ricarica come il v1 (renderVoci)
    } catch (e) {
      A.toast(AV_TEXTS.toastClassificaFallita + (e && e.message ? e.message : e)); // verbatim r.2825
    } finally {
      busyClassRef.current = false;
      setClassificando(false);
    }
  };

  const onExportCsv = async () => {
    if (busyCsvRef.current) return;
    busyCsvRef.current = true;
    try {
      try { await esportaCsvVoci(win, anno, categoria); }
      catch (e) { A.toast(AV_TEXTS.toastExportFallito + (e && e.message ? e.message : e)); } // verbatim r.2831
    } finally {
      busyCsvRef.current = false;
    }
  };

  const rigaDipendente = (r, i) => (
    <tr key={r.dipendente_id || i} className="border-t hover:bg-cyan-50">
      <td className="px-3 py-2 text-sm">{nomeRiga(r)}</td>
      {(r.mesi || []).map((m, mi) => (
        <td key={mi} className={'px-2 py-2 text-right text-xs tabular-nums ' + (m ? 'text-slate-700' : 'text-slate-300')}>{m ? f2(m) : '—'}</td>
      ))}
      <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums" style={{ color: '#0e7490' }}>{f2(r.totale)}</td>
    </tr>
  );

  const rigaTotaleCategoria = (g) => {
    const totMesi = totaliMesiCategoria(g);
    const totCat = (vista.totali_per_categoria || {})[g.categoria] || 0;
    return (
      <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
        <td className="px-3 py-2 text-sm">{AV_TEXTS.totalePre + labelCat(g.categoria)}</td>
        {totMesi.map((m, i) => (
          <td key={i} className="px-2 py-2 text-right text-xs tabular-nums">{f2(m)}</td>
        ))}
        <td className="px-3 py-2 text-right text-sm tabular-nums" style={{ color: '#0e7490' }}>{f2(totCat)}</td>
      </tr>
    );
  };

  const tabellaCategoria = (g) => (
    <div key={g.categoria} className="bg-white border rounded-lg overflow-hidden mb-4">
      <div className="px-4 py-2.5 border-b bg-slate-50 font-medium text-sm">{labelCat(g.categoria)}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-3 py-2 text-xs uppercase font-medium text-left">{AV_TEXTS.colDipendente}</th>
              {MESI_S.map((m) => <th key={m} className="px-2 py-2 text-xs uppercase font-medium text-right">{m}</th>)}
              <th className="px-3 py-2 text-xs uppercase font-medium text-right">{AV_TEXTS.colTotale}</th>
            </tr>
          </thead>
          <tbody>
            {g.righe.map(rigaDipendente)}
            {rigaTotaleCategoria(g)}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div data-amm-v3="voci">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">{AV_TEXTS.title}</h1>
          <div className="text-xs text-slate-400">{AV_TEXTS.sub}</div>
        </div>
        <div className="flex items-center gap-2">
          <button id="voci-classifica" type="button" disabled={classificando} className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs px-3 py-2 rounded font-medium" onClick={onClassifica}>{classificando ? AV_TEXTS.classificaBusy : AV_TEXTS.classifica}</button>
          <button id="amm-export-csv" type="button" className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs px-3 py-2 rounded font-medium" onClick={onExportCsv}>{AV_TEXTS.exportCsv}</button>
          <div className="flex items-center gap-1">
            <button id="voci-prev" type="button" className="px-2 py-1 border rounded text-sm bg-white" onClick={() => setPending({ ...pending, anno: pending.anno - 1 })}>{'‹'}</button>
            <span className="px-2 font-semibold tabular-nums">{anno}</span>
            <button id="voci-next" type="button" className="px-2 py-1 border rounded text-sm bg-white" onClick={() => setPending({ ...pending, anno: pending.anno + 1 })}>{'›'}</button>
          </div>
        </div>
      </div>

      {/* D-A4.5: ricarica fallita con dati presenti ⇒ banner + Riprova */}
      {dati.error && vista && (
        <div role="alert" className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-3" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          <span>{AM_NEW_TEXTS.erroreRicarica + (dati.error.message || String(dati.error))}</span>
          <button type="button" style={{ background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }} onClick={() => setReloadN((n) => n + 1)}>{AM_NEW_TEXTS.riprova}</button>
        </div>
      )}

      {!vista ? (
        <StatoBox error={dati.error} onRetry={() => setReloadN((n) => n + 1)} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <Kpi label={AV_TEXTS.kpiTotale + anno} val={f2(vista.totale)} color="#0e7490" />
            <Kpi label={AV_TEXTS.kpiCategorie} val={gruppi.length} color="#0f172a" />
          </div>
          <div className="flex gap-2 flex-wrap items-center mb-4">
            {CHIP_CATEGORIE.map((c) => {
              const on = c === categoria;
              return (
                <span
                  key={c || '__tutte'}
                  className={'amm-fchip amm-voci-chip cursor-pointer text-xs px-3 py-1.5 rounded-full border ' + (on ? '' : 'bg-white text-slate-600 border-slate-200')}
                  data-cat={c}
                  style={on ? { background: '#ecfeff', color: '#0e7490', borderColor: '#a5f3fc' } : undefined}
                  onClick={() => setPending({ ...pending, categoria: c })}
                >{chipLabel(c)}</span>
              );
            })}
          </div>
          {gruppi.length
            ? gruppi.map(tabellaCategoria)
            : <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm mb-4">{emptyMessage(anno, categoria)}</div>}
        </>
      )}
    </div>
  );
}

// Factory (pattern createAmmV3Resoconto)
export function createAmmV3Voci(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;

  function render() {
    const container = win.document.getElementById('amm-page');
    if (!container) throw new Error('#amm-page assente');
    const manomesso = root && mountedEl === container && tick > 0 && !container.querySelector('[data-amm-v3]');
    if (!root || mountedEl !== container || !container.isConnected || manomesso) {
      if (root) { try { root.unmount(); } catch (e) { /* root già ferito/smontato */ } }
      container.replaceChildren();
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1;
    root.render(<VociView win={win} tick={tick} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
